import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback, useState } from 'react';
import { budgetReducer, initialState } from './budgetReducer';
import { loadBudget, saveBudget, clearBudget } from '../utils/localStorage';
import { useDerivedState } from './selectors';

const BudgetContext = createContext(null);

export function BudgetProvider({ children }) {
  const [state, dispatch] = useReducer(budgetReducer, initialState);

  const derived = useDerivedState(state);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Hydration-safe: load saved budget from localStorage in useEffect
  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    const saved = loadBudget();
    if (saved) dispatch({ type: 'HYDRATE', payload: saved });
  }, []);

  // Debounced auto-save on state change
  const saveTimer = useRef(null);
  useEffect(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveBudget(state);
      setLastSavedAt(new Date());
    }, 500);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [state]);

  const resetAndClear = useCallback(() => {
    dispatch({ type: 'RESET_ALL' });
    clearBudget();
    setLastSavedAt(null);
  }, []);

  return (
    <BudgetContext.Provider value={{ state, dispatch, derived, resetAndClear, lastSavedAt }}>
      {children}
    </BudgetContext.Provider>
  );
}

export function useBudget() {
  const context = useContext(BudgetContext);
  if (!context) throw new Error('useBudget must be used within BudgetProvider');
  return context;
}
